
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ArticlePreviewProps {
  title: string;
  content: string;
  lead?: string;
  headerImageUrl?: string;
}

const ArticlePreview: React.FC<ArticlePreviewProps> = ({ 
  title, 
  content, 
  lead,
  headerImageUrl 
}) => {
  // Function to safely parse content from Editor.js format if needed
  const renderContent = () => {
    if (!content) return <p className="text-gray-400">Your article preview will appear here...</p>;
    
    // Try to determine if content is from Editor.js (JSON format)
    try {
      const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
      
      if (parsedContent && parsedContent.blocks) {
        // Render blocks from Editor.js format
        return (
          <div>
            {parsedContent.blocks.map((block: any, index: number) => {
              switch (block.type) {
                case 'header':
                  const HeaderTag = `h${block.data.level}` as keyof JSX.IntrinsicElements;
                  return <HeaderTag key={index} className="mt-6 mb-4 font-bold">{block.data.text}</HeaderTag>;
                
                case 'paragraph':
                  return <p key={index} className="mb-4" dangerouslySetInnerHTML={{ __html: block.data.text }} />;
                
                case 'list':
                  if (block.data.style === 'ordered') {
                    return (
                      <ol key={index} className="list-decimal pl-6 mb-4">
                        {block.data.items.map((item: string, i: number) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                        ))}
                      </ol>
                    );
                  }
                  return (
                    <ul key={index} className="list-disc pl-6 mb-4">
                      {block.data.items.map((item: string, i: number) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  );
                
                case 'image':
                  return (
                    <figure key={index} className="mb-6">
                      <img 
                        src={block.data.file?.url || block.data.url} 
                        alt={block.data.caption || title}
                        className="w-full h-auto rounded-md" 
                      />
                      {block.data.caption && (
                        <figcaption className="text-sm text-center text-gray-500 mt-2">
                          {block.data.caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                
                case 'quote':
                  return (
                    <blockquote key={index} className="border-l-4 border-primary pl-4 italic my-4">
                      <p dangerouslySetInnerHTML={{ __html: block.data.text }} />
                      {block.data.caption && (
                        <cite className="block text-right">— {block.data.caption}</cite>
                      )}
                    </blockquote>
                  );
                
                default:
                  return <p key={index} className="mb-4">{JSON.stringify(block.data)}</p>;
              }
            })}
          </div>
        );
      }
    } catch (e) {
      // Not JSON or not in Editor.js format, treat as plain text
    }
    
    // Fallback to rendering as plain text
    return <div className="whitespace-pre-wrap">{content}</div>;
  };

  return (
    <div className="tabs-preview">
      <Tabs defaultValue="desktop">
        <TabsList className="mb-4">
          <TabsTrigger value="desktop">Desktop</TabsTrigger>
          <TabsTrigger value="mobile">Mobile</TabsTrigger>
        </TabsList>
        <TabsContent value="desktop">
          <div className="bg-white dark:bg-gray-800 p-6 rounded min-h-[500px] border">
            {headerImageUrl && (
              <div className="mb-6 aspect-video rounded-md overflow-hidden">
                <img 
                  src={headerImageUrl} 
                  alt={title} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/1200x675?text=Image+Not+Found';
                  }}
                />
              </div>
            )}
            <h1 className="text-3xl font-bold mb-4">{title || "Your Article Title"}</h1>
            {lead && (
              <p className="text-lg font-medium italic border-l-4 border-primary pl-4 mb-6">
                {lead}
              </p>
            )}
            <div className="prose dark:prose-invert max-w-none">
              {renderContent()}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="mobile">
          <div className="mx-auto max-w-[375px] bg-white dark:bg-gray-800 p-4 rounded min-h-[500px] border">
            {headerImageUrl && (
              <div className="mb-4 aspect-video rounded-md overflow-hidden">
                <img 
                  src={headerImageUrl} 
                  alt={title} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/1200x675?text=Image+Not+Found';
                  }}
                />
              </div>
            )}
            <h1 className="text-2xl font-bold mb-3">{title || "Your Article Title"}</h1>
            {lead && (
              <p className="text-base font-medium italic border-l-4 border-primary pl-3 mb-4">
                {lead}
              </p>
            )}
            <div className="prose dark:prose-invert max-w-none prose-sm">
              {renderContent()}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ArticlePreview;
